import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import {
  BarChart3, Loader2, DollarSign, Building2, CalendarCheck, CalendarDays,
  TrendingUp, TrendingDown, CheckCircle2, Clock, XCircle, Percent,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { managerApi, type ManagerDashboardResponse } from "@/lib/api";
import { formatNaira } from "@/data/venues";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend, AreaChart, Area,
} from "recharts";

export const Route = createFileRoute("/admin/manager-analytics")({
  component: ManagerAnalyticsPage,
});

const STATUS_COLORS: Record<string, string> = {
  confirmed: "#22c55e",
  pending: "#f59e0b",
  cancelled: "#ef4444",
  checked_in: "#3b82f6",
  checked_out: "#8b5cf6",
  refunded: "#ec4899",
  manager_approval_pending: "#f97316",
};

function KpiCard({ icon: Icon, label, value, sub, color }: {
  icon: React.ElementType; label: string; value: string; sub?: string; color?: string;
}) {
  return (
    <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
      <div className="grid size-10 place-items-center rounded-lg bg-accent text-primary">
        <Icon className="size-5" style={{ color }} />
      </div>
      <div className="mt-4 font-[family-name:Inter] text-2xl font-bold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
      {sub && <div className="mt-1 text-[11px] text-muted-foreground/70">{sub}</div>}
    </div>
  );
}

function ManagerAnalyticsPage() {
  const { user } = useAuth();
  const [data, setData] = useState<ManagerDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.id) { setLoading(false); return; }
    managerApi.dashboard(user.id)
      .then(res => setData(res))
      .catch(err => setError(err instanceof Error ? err.message : "Failed to load analytics"))
      .finally(() => setLoading(false));
  }, [user?.id]);

  const commissionRate = data?.commissionRate ?? 0.10;
  const commissionPct = Math.round(commissionRate * 100);

  const monthlyRevenue = useMemo(() => {
    if (!data?.recentBookings.length) return [];
    const map: Record<string, number> = {};
    for (const b of data.recentBookings) {
      const key = b.createdAt.slice(0, 7);
      map[key] = (map[key] ?? 0) + Number(b.totalAmount);
    }
    return Object.entries(map)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, revenue]) => ({
        label: new Date(month + "-01").toLocaleDateString("en-NG", { month: "short", year: "numeric" }),
        revenue,
      }));
  }, [data?.recentBookings]);

  const statusData = useMemo(() => {
    if (!data?.recentBookings.length) return [];
    const counts: Record<string, number> = {};
    for (const b of data.recentBookings) {
      const s = b.status.toLowerCase();
      counts[s] = (counts[s] ?? 0) + 1;
    }
    return Object.entries(counts).map(([status, count]) => ({
      name: status.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
      value: count,
      color: STATUS_COLORS[status] || "#94a3b8",
    }));
  }, [data?.recentBookings]);

  const venueData = useMemo(() => {
    if (!data?.venues.length) return [];
    return data.venues
      .filter(v => v.revenue > 0)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 8)
      .map(v => ({
        name: v.name.length > 18 ? v.name.slice(0, 18) + "…" : v.name,
        revenue: v.revenue,
        bookings: v.bookingCount,
      }));
  }, [data?.venues]);

  const serviceChargeVsNet = useMemo(() => {
    if (!data) return [];
    return [
      { name: "Service Charge", value: data.totalCommission, fill: "#ef4444" },
      { name: "Net Earnings", value: data.netEarnings, fill: "#22c55e" },
    ];
  }, [data]);

  const hasActivity = (data?.totalBookings ?? 0) > 0 || (data?.totalRevenue ?? 0) > 0;

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8">
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-20">
          <Loader2 className="size-4 animate-spin" /> Loading analytics…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8">
        <div className="text-sm text-destructive py-20">{error}</div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8">
      <div>
        <h1 className="font-display text-2xl font-bold sm:text-3xl">My Analytics</h1>
        <p className="text-sm text-muted-foreground">Your personal financial and operational performance.</p>
      </div>

      {!hasActivity ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-border/60 bg-card py-24 text-center shadow-soft">
          <BarChart3 className="mb-4 size-14 text-muted-foreground/30" />
          <p className="text-lg font-semibold">No data yet</p>
          <p className="mt-2 max-w-sm text-sm text-muted-foreground">
            Analytics will appear once guests start booking your venues.
          </p>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            <KpiCard icon={DollarSign} label="Total Revenue" value={formatNaira(data.totalRevenue)} sub="venue + event" color="#22c55e" />
            <KpiCard icon={Building2} label="Venue Revenue" value={formatNaira(data.venueRevenue)} sub="bookings" color="#16a34a" />
            <KpiCard icon={CalendarDays} label="Event Revenue" value={formatNaira(data.eventRevenue)} sub="tickets" color="#10b981" />
            <KpiCard icon={DollarSign} label="30-Day Earnings" value={formatNaira(data.earnings30Days)} sub={`${formatNaira(data.venueEarnings30Days)} venue · ${formatNaira(data.eventEarnings30Days)} event`} color="#059669" />
            <KpiCard icon={Percent} label="Service Charge" value={formatNaira(data.totalCommission)} sub={`${commissionPct}% rate`} color="#ef4444" />
            <KpiCard icon={DollarSign} label="Net Earnings" value={formatNaira(data.netEarnings)} sub={`${formatNaira(data.venueNetEarnings)} venue · ${formatNaira(data.eventNetEarnings)} event`} color="#3b82f6" />
            <KpiCard icon={CalendarCheck} label="Total Bookings" value={String(data.totalBookings)} sub={`${data.confirmedBookings} confirmed`} color="#f59e0b" />
            <KpiCard icon={Building2} label="My Venues" value={String(data.totalVenues)} color="#8b5cf6" />
          </div>

          {/* Charts Row 1 */}
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Monthly Revenue */}
            <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
              <h2 className="font-display text-base font-semibold mb-4">Monthly Revenue</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={monthlyRevenue}>
                    <defs>
                      <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#7c3aed" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `₦${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v: number) => formatNaira(v)} />
                    <Area type="monotone" dataKey="revenue" stroke="#7c3aed" strokeWidth={2} fill="url(#revGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Revenue by Venue */}
            <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
              <h2 className="font-display text-base font-semibold mb-4">Revenue by Venue</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={venueData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v: number) => `₦${(v / 1000).toFixed(0)}k`} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
                    <Tooltip formatter={(v: number) => formatNaira(v)} />
                    <Bar dataKey="revenue" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Charts Row 2 */}
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Booking Status Breakdown */}
            <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
              <h2 className="font-display text-base font-semibold mb-4">Booking Status</h2>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius="80%" label>
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" height={24} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Service Charge vs Net Earnings */}
            <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
              <h2 className="font-display text-base font-semibold mb-4">Service Charge vs Net</h2>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={serviceChargeVsNet}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `₦${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v: number) => formatNaira(v)} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {serviceChargeVsNet.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Overview Stats */}
            <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft space-y-4">
              <h2 className="font-display text-base font-semibold">Overview</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between rounded-xl bg-green-50 px-4 py-3">
                  <div className="flex items-center gap-2 text-sm text-green-700">
                    <CheckCircle2 className="size-4" /> Confirmed
                  </div>
                  <span className="font-bold text-green-700 font-[family-name:Inter]">{data.confirmedBookings}</span>
                </div>
                <div className="flex items-center justify-between rounded-xl bg-amber-50 px-4 py-3">
                  <div className="flex items-center gap-2 text-sm text-amber-700">
                    <Clock className="size-4" /> Pending
                  </div>
                  <span className="font-bold text-amber-700 font-[family-name:Inter]">{data.pendingBookings}</span>
                </div>
                <div className="flex items-center justify-between rounded-xl bg-blue-50 px-4 py-3">
                  <div className="flex items-center gap-2 text-sm text-blue-700">
                    <TrendingUp className="size-4" /> 30-Day Bookings
                  </div>
                  <span className="font-bold text-blue-700 font-[family-name:Inter]">{data.bookings30Days}</span>
                </div>
                <div className="flex items-center justify-between rounded-xl bg-purple-50 px-4 py-3">
                  <div className="flex items-center gap-2 text-sm text-purple-700">
                    <Percent className="size-4" /> Service Charge Rate
                  </div>
                  <span className="font-bold text-purple-700 font-[family-name:Inter]">{commissionPct}%</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
